import Button from '@rio-cloud/rio-uikit/Button';
import orderBy from 'lodash/orderBy';
import type { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    FeatureToggles,
    usePocketDriverRegistrationMaintenanceToggle,
    useToggle,
} from '../../../../../configuration/featureToggle/toggleHooks';
import { getTenant } from '../../../../../configuration/login/loginSlice';
import { useAppSelector } from '../../../../../configuration/setup/hooks';
import { DeviceType } from '../details/types';
import { PrerequisiteNotes } from './PrerequisiteNotes';
import { PocketDriverMaintenanceBanner } from './PocketDriverMaintenanceBanner';
import { TelematicsList } from './TelematicsList';
import { isPocketDriverRegistrationMaintenanceActive } from './pocketDriverMaintenance';
import { getMissingTelematicTrackingKey } from './trackingKeys';
import UnderConstruction from './UnderConstruction';
import { ALL_TELEMATICS_TILES, TELEMATICS_TILES_DISPLAYED_FIRST } from './configuration/telematicsTileConfig';
import type { AssetTypeTile, BrandTile, TelematicsTile, typeBrandCombination } from './configuration/types';

//Todo find a way to adapt to selected language when new contact form is in place
const CONTACT_FORMULAR_URL = 'https://rio.cloud/de/contact/form?opener=request-for-new-telematic';

interface FormStepSelectTelematicsProps {
    selectedAssetId: string | undefined;
    selectedBrand: BrandTile | undefined;
    selectedTelematic: TelematicsTile | undefined;
    selectedAssetType: AssetTypeTile | undefined;
    setSelectedTelematic: (value: TelematicsTile | undefined) => void;
}

export const FormStepSelectTelematics: FunctionComponent<FormStepSelectTelematicsProps> = props => {
    const { selectedBrand, selectedAssetType, selectedTelematic, selectedAssetId, setSelectedTelematic } = props;
    const tenant = useAppSelector(getTenant);
    const { value: commaSeparatedEnabledTelematicsTiles, loading: isFeatureFlagLoading } = useToggle(
        FeatureToggles.telematicstiles,
        ''
    );
    const maintenanceEnabled = usePocketDriverRegistrationMaintenanceToggle();

    const displayedTelematics = getDisplayedTelematicsTiles(
        selectedAssetType,
        selectedBrand,
        selectedAssetId,
        tenant,
        commaSeparatedEnabledTelematicsTiles,
        isFeatureFlagLoading
    );
    const showPocketDriverMaintenance = isPocketDriverRegistrationMaintenanceActive(
        selectedTelematic,
        maintenanceEnabled
    );

    return (
        // biome-ignore lint/a11y/useAriaPropsSupportedByRole: potentially used for tests
        <div className='modal-body bg-lightest padding-0' aria-label='dialog body'>
            <div>
                <div className='padding-20'>
                    {showPocketDriverMaintenance && <PocketDriverMaintenanceBanner />}
                    <div className='display-grid grid-cols-2 gap-20'>
                        <div className='display-flex flex-column min-height-500'>
                            <TelematicsList
                                displayedTelematics={displayedTelematics}
                                selectedTelematic={selectedTelematic}
                                selectedAssetType={selectedAssetType}
                                selectedBrand={selectedBrand}
                                setSelectedTelematic={setSelectedTelematic}
                            />
                            <Button
                                className='btn-multiline'
                                multiline
                                autoTrackingKey={getMissingTelematicTrackingKey(selectedAssetType, selectedBrand)}
                                onClick={() => window.open(CONTACT_FORMULAR_URL)}
                            >
                                <div className='padding-10'>
                                    <FormattedMessage id='intl-msg:create-new-dialog.missing-telematic' />
                                </div>
                                <span className='uikit-icon icon-support' />
                            </Button>
                        </div>
                        <div className='panel panel-default panel-body display-flex flex-column'>
                            {selectedTelematic ? (
                                <PrerequisiteNotes selectedTelematic={selectedTelematic} />
                            ) : (
                                <UnderConstruction />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const isFeatureToggledTileEnabled = (
    telematicsTile: TelematicsTile,
    commaSeparatedEnabledTelematicsTiles: string | number | boolean,
    isFeatureFlagLoading: boolean
) => {
    if (typeof commaSeparatedEnabledTelematicsTiles !== 'string') {
        console.warn(`unexpected type for telematic-tile-toggle: ${typeof commaSeparatedEnabledTelematicsTiles}`);
        return false;
    }

    if (isFeatureFlagLoading) {
        return false;
    } else {
        return (
            !telematicsTile.featureToggleName ||
            commaSeparatedEnabledTelematicsTiles
                .split(',')
                .map(it => it.trim())
                .includes(telematicsTile.featureToggleName)
        );
    }
};

const getTenantSpecificTiles = (tenant: string | undefined) =>
    ALL_TELEMATICS_TILES.filter(tile => tile.suitableTenants.some(suitableTenant => suitableTenant === tenant));

const getOemTiles = (
    selectedAssetType: AssetTypeTile | undefined,
    selectedBrand: BrandTile | undefined,
    tenant: string | undefined
) => {
    const isMatch = (it: typeBrandCombination): boolean => it.type === selectedAssetType && it.brand === selectedBrand;

    const tenantSpecificTelematicTiles = getTenantSpecificTiles(tenant);

    return tenantSpecificTelematicTiles.filter((telematics: TelematicsTile): boolean =>
        telematics.oemFor.some(isMatch)
    );
};

const getTelematicsTiles = (
    selectedAssetType: AssetTypeTile | undefined,
    selectedBrand: BrandTile | undefined,
    selectedAssetId: string | undefined,
    tenant: string | undefined
) => {
    const isMatch = (it: typeBrandCombination): boolean =>
        (it.type === selectedAssetType || selectedAssetType === undefined) &&
        (it.brand === selectedBrand || selectedBrand === undefined);

    // if device is added to existing asset, it does not make sense to offer 'no telematics' device type
    const tenantSpecificTelematicTiles = getTenantSpecificTiles(tenant).filter(telematicsTile =>
        selectedAssetId !== undefined ? telematicsTile.deviceType !== DeviceType.NONE : true
    );

    return tenantSpecificTelematicTiles.filter((telematics: TelematicsTile): boolean =>
        telematics.suitableFor.some(isMatch)
    );
};

const getDisplayedTelematicsTiles = (
    selectedAssetType: AssetTypeTile | undefined,
    selectedBrand: BrandTile | undefined,
    selectedAssetId: string | undefined,
    tenant: string | undefined,
    commaSeparatedEnabledTelematicsTiles: string | number | boolean,
    isFeatureFlagLoading: boolean
): TelematicsTile[] => {
    const eligibleTiles = getEligibleTelematicsTiles(selectedAssetType, selectedBrand, selectedAssetId, tenant);

    return eligibleTiles.filter(telematicsTile =>
        isFeatureToggledTileEnabled(telematicsTile, commaSeparatedEnabledTelematicsTiles, isFeatureFlagLoading)
    );
};

export const getEligibleTelematicsTiles = (
    selectedAssetType: AssetTypeTile | undefined,
    selectedBrand: BrandTile | undefined,
    selectedAssetId: string | undefined,
    tenant: string | undefined
): TelematicsTile[] => {
    const oemTiles = getOemTiles(selectedAssetType, selectedBrand, tenant);

    const eligibleTiles = getTelematicsTiles(selectedAssetType, selectedBrand, selectedAssetId, tenant);

    const fixedEligibleTiles = TELEMATICS_TILES_DISPLAYED_FIRST.filter(tile => eligibleTiles.includes(tile));

    const otherEligibleTiles = eligibleTiles
        .filter(tile => !oemTiles.includes(tile))
        .filter(tile => !TELEMATICS_TILES_DISPLAYED_FIRST.includes(tile));

    return oemTiles.concat(fixedEligibleTiles).concat(orderBy(otherEligibleTiles, 'displayName', 'asc'));
};
